import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Container from "react-bootstrap/Container";
import { useCart } from "../../contexts/Cart";
import useLanguage from "../../hooks/useLanguage";
import useFitText from "../../hooks/useFitText";
import { ArrowUturnLeftIcon } from "@heroicons/react/24/outline";
import { ShoppingCart } from "lucide-react";
import { DEFAULT_LANGUAGE, pathInLanguage } from "../../contexts/Language";
import localizedLink from "../../utils/localizedLink";
import { GeorgianFlag, UKFlag } from "./Flags";

// Light gray pills, like eufy.com's secondary buttons.
const HEADER_BUTTON_CLASS_NAME =
  "flex h-10 shrink-0 items-center justify-center gap-2 rounded-full bg-[#f5f6f7] text-[#1e2024] transition-colors hover:bg-[#eaeaec] md:h-11";

export default function Header() {
  const { t } = useTranslation();
  const { cartItems } = useCart();
  const { language, changeLanguage } = useLanguage();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const isHome = pathInLanguage(pathname, DEFAULT_LANGUAGE) === "/";
  const hideAnimation = pathname.includes("/cart") || pathname.includes("/order");
  const totalQuantity = cartItems.reduce(
    (total, item) => total + item.quantity,
    0
  );
  const titleRef = useFitText(t("header.eufy-georgia"));

  return (
    <header className="site-header">
      {/* 6px gaps on phones leave room for the title on one line; Bootstrap's
          !important gap-2 would force 8px. */}
      <Container className="flex items-center gap-[6px] sm:gap-[8px] md:px-5">
        {!isHome && (
          <button
            type="button"
            aria-label="Back"
            onClick={() => {
              // React Router numbers history entries in history.state.idx; 0
              // means the site was opened on this page (typed URL, new tab,
              // external link), so there's no page of ours to go back to.
              if (window.history.state?.idx > 0) {
                navigate(-1);
              } else {
                navigate(localizedLink("/", language));
              }
            }}
            className="mr-1 shrink-0 sm:mr-2"
          >
            <ArrowUturnLeftIcon style={{ width: 25 }} />
          </button>
        )}

        <div className="min-w-0 flex-1">
          <Link to={localizedLink("/", language)}>
            {/* Never wraps: shrinks below this size when space runs out. */}
            <h2
              ref={titleRef}
              className="whitespace-nowrap text-lg leading-tight md:text-xl"
            >
              {t("header.eufy-georgia")}
            </h2>
          </Link>
          {/* Hidden on phones, where it would wrap to 2-3 lines and crowd the
              header. Wraps (balanced) below md: the Georgian slogan never fits
              one line there. */}
          <p className="mb-0 mt-1 hidden text-balance text-xs leading-tight text-[#6d6e71] sm:block md:truncate md:text-sm">
            {t("header.slogan")}
          </p>
        </div>

        <button
          type="button"
          aria-label={language === DEFAULT_LANGUAGE ? "English" : "ქართული"}
          title={language === DEFAULT_LANGUAGE ? "English" : "ქართული"}
          className={`${HEADER_BUTTON_CLASS_NAME} w-10 md:w-11`}
          onClick={() => {
            if (language === DEFAULT_LANGUAGE) {
              changeLanguage("en");
            } else {
              changeLanguage(DEFAULT_LANGUAGE);
            }
          }}
        >
          {language === DEFAULT_LANGUAGE ? <UKFlag /> : <GeorgianFlag />}
        </button>

        <Link
          to={localizedLink("/cart", language)}
          className={`${HEADER_BUTTON_CLASS_NAME} w-10 sm:w-auto sm:gap-3 sm:px-4`}
        >
          <span className="relative">
            <ShoppingCart className="h-6 w-6" strokeWidth={1.5} />
            {totalQuantity > 0 && (
              <span className="absolute right-0.5 -top-[3px] flex">
                {!hideAnimation && (
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#080a0f] opacity-40"></span>
                )}
                <span className="relative flex h-4 min-w-4 items-center justify-center rounded-full bg-[#080a0f] px-[3px] text-[11px] font-bold leading-none text-white">
                  {totalQuantity}
                </span>
              </span>
            )}
          </span>
          <span className="sr-only text-sm sm:not-sr-only md:text-base">
            {t("header.cart")}
          </span>
        </Link>
      </Container>
    </header>
  );
}
